import { activityService } from '@/services/activityService';

// Utility functions for tracking activities throughout the application
export class ActivityTracker {
  // Device-related activities
  static async trackDeviceAdded(deviceName: string, area: string): Promise<void> {
    await activityService.addDeviceActivity(area, deviceName, 'added');
  }

  static async trackDeviceRemoved(deviceName: string, area: string): Promise<void> {
    await activityService.addDeviceActivity(area, deviceName, 'removed');
  }

  static async trackDeviceTurnedOn(deviceName: string, area: string): Promise<void> {
    await activityService.addDeviceActivity(area, deviceName, 'turned_on');
  }

  static async trackDeviceTurnedOff(deviceName: string, area: string): Promise<void> {
    await activityService.addDeviceActivity(area, deviceName, 'turned_off');
  }

  static async trackDeviceConnected(deviceName: string, area: string): Promise<void> {
    await activityService.addDeviceActivity(area, deviceName, 'connected');
  }

  static async trackDeviceDisconnected(deviceName: string, area: string): Promise<void> {
    await activityService.addDeviceActivity(area, deviceName, 'disconnected');
  }

  // Motion sensor activities
  static async trackMotionDetected(area: string, sensorName?: string): Promise<void> {
    await activityService.addMotionActivity(area, sensorName);
  }

  // Daylight sensor activities
  static async trackDaylightReading(area: string, luxValue: number, sensorName?: string): Promise<void> {
    await activityService.addDaylightActivity(area, luxValue, sensorName);
  }

  // Scene activities
  static async trackSceneActivated(sceneName: string, area: string): Promise<void> {
    await activityService.addSceneActivity(area, sceneName, 'activated');
  }

  static async trackSceneDeactivated(sceneName: string, area: string): Promise<void> {
    await activityService.addSceneActivity(area, sceneName, 'deactivated');
  }

  // Shade activities
  static async trackShadeOpened(shadeName: string, area: string): Promise<void> {
    await activityService.addShadeActivity(area, shadeName, 'opened');
  }

  static async trackShadeClosed(shadeName: string, area: string): Promise<void> {
    await activityService.addShadeActivity(area, shadeName, 'closed');
  }

  static async trackShadeAdjusted(shadeName: string, area: string): Promise<void> {
    await activityService.addShadeActivity(area, shadeName, 'adjusted');
  }

  // Alert activities
  static async trackAlertCreated(alertName: string, area: string): Promise<void> {
    await activityService.addAlertActivity(area, alertName, 'created');
  }

  static async trackAlertTriggered(alertName: string, area: string): Promise<void> {
    await activityService.addAlertActivity(area, alertName, 'triggered');
  }

  static async trackAlertResolved(alertName: string, area: string): Promise<void> {
    await activityService.addAlertActivity(area, alertName, 'resolved');
  }

  // Floorplan activities
  static async trackFloorplanCreated(): Promise<void> {
    await activityService.addFloorplanActivity('created');
  }

  static async trackFloorplanUpdated(): Promise<void> {
    await activityService.addFloorplanActivity('updated');
  }

  // System activities
  static async trackSystemEvent(message: string, area: string = 'System'): Promise<void> {
    await activityService.addSystemActivity(message, area);
  }

  static async trackSystemError(errorMessage: string, area: string = 'System'): Promise<void> {
    await activityService.addSystemActivity(`System Error: ${errorMessage}`, area);
  }

  // Energy activities
  static async trackEnergyEvent(area: string, message: string, details?: any): Promise<void> {
    await activityService.addEnergyActivity(area, message, details);
  }

  static async trackHighEnergyConsumption(area: string, consumption: number): Promise<void> {
    await activityService.addEnergyActivity(area, `High energy consumption detected: ${consumption} kWh`, { consumption });
  }

  static async trackEnergyNormalized(area: string): Promise<void> {
    await activityService.addEnergyActivity(area, 'Energy usage normalized');
  }

  // Climate activities
  static async trackTemperatureAlert(area: string, temperature: number, threshold: number): Promise<void> {
    const message = temperature > threshold 
      ? `Temperature above threshold: ${temperature}°C (threshold: ${threshold}°C)`
      : `Temperature below threshold: ${temperature}°C (threshold: ${threshold}°C)`;
    await activityService.addActivity('climate', message, area, { temperature, threshold });
  }

  static async trackHumidityAlert(area: string, humidity: number, threshold: number): Promise<void> {
    const message = humidity > threshold 
      ? `Humidity above threshold: ${humidity}% (threshold: ${threshold}%)`
      : `Humidity below threshold: ${humidity}% (threshold: ${threshold}%)`;
    await activityService.addActivity('climate', message, area, { humidity, threshold });
  }

  // Generic activity method
  static async trackActivity(type: string, message: string, area: string, details?: any): Promise<void> {
    await activityService.addActivity(type, message, area, details);
  }
}

// Export the activity service for direct access if needed
export { activityService };
